import React, { useCallback, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import QuizContent from '../shared/QuizContent'
import QuizTitle from '../shared/QuizTitle'
import LazyImage from '../shared/LazyImage'
import stepSisSvg from '../../assets/StepSis.svg'
import bestFriendSvg from '../../assets/bestFriend.svg'
import closeFriendSvg from '../../assets/closeFriend.svg'
import caregiverSvg from '../../assets/Caregiver.svg'
import jesterSvg from '../../assets/Jester.svg'
import innocentSvg from '../../assets/Innocent.svg'
import starsIcon from '../../assets/stars.svg'
import giftIcon from '../../assets/gift.svg'
import { useQuizSelections } from '../../hooks/useLocalStorage'
import { createModelParams, getGoogleAuthUrl, getExternalClickIds, LP_SOURCE } from '../../utils/api'
import { getEfTransactionId } from '../../utils/everflow'
import { getAuthToken, getStorage } from '../../utils/storage'
import { buildPaywallUrl, buildOAuthStatePath, getNavType, resolvePaywallPath } from '../../utils/paywall'
import { trackStep6 } from '../../utils/analytics'
import quizMainRealistic from '../../assets/quiz/realistic/main.mp4'
import quizMainAnime from '../../assets/quiz/anime/main.mp4'
import { CHARACTER_MEDIA, CHARACTER_LABELS } from './QuizCharacter'

const VIDEOS = {
  Realistic: quizMainRealistic,
  Anime: quizMainAnime,
}

const PERSONALITY_ICONS_QUIZ = {
  gamerBuddy: stepSisSvg,
  closeFriend: closeFriendSvg,
  bestFriend: bestFriendSvg,
}

const PERSONALITY_TRAIT_ICONS = {
  caregiver: caregiverSvg,
  jester: jesterSvg,
  innocent: innocentSvg,
}

const PERSONALITY_TRAIT_LABELS = {
  caregiver: 'Sweet',
  jester: 'Sarcastic',
  innocent: 'Geeky',
}

const RELATIONSHIP_LABELS = {
  gamerBuddy: 'Gamer Buddy',
  closeFriend: 'Close Friend',
  bestFriend: 'Best Friend',
}

const renderPersonalityIcon = personality => {
  const src = PERSONALITY_ICONS_QUIZ[personality] || PERSONALITY_ICONS_QUIZ.bestFriend
  return (
    <div className='icon-badge'>
      <img src={src} alt={personality || 'personality'} />
    </div>
  )
}

const renderPersonalityTraitIcon = trait => {
  const src = PERSONALITY_TRAIT_ICONS[trait]
  if (!src) return null
  return (
    <div className='icon-badge'>
      <img src={src} alt={trait} />
    </div>
  )
}

const QuizFinal = ({ onPrevious }) => {
  const { t } = useTranslation()
  const [selections] = useQuizSelections()
  const [isRedirecting, setIsRedirecting] = useState(false)

  // Final quiz screen reached — fire `step_6` once on mount.
  const step6FiredRef = useRef(false)
  useEffect(() => {
    if (!step6FiredRef.current) {
      step6FiredRef.current = true
      trackStep6()
    }
  }, [])

  const welcome = selections?.welcome || 'Realistic'
  const character = selections?.character || null
  const personality = selections?.step4?.personality
  const personalityTrait = selections?.step4?.personalityTrait
  const personalityTraitLabel = selections?.step4?.personalityTraitLabel || PERSONALITY_TRAIT_LABELS[personalityTrait]

  const handleBringToLife = useCallback(async () => {
    if (isRedirecting) return
    setIsRedirecting(true)

    const authToken = localStorage.getItem('quizAuthToken') || getAuthToken()
    const pendingGoogleSignup = localStorage.getItem('pendingGoogleSignup') === 'true'

    console.log('[quiz] Bring-to-life clicked', { hasAuthToken: !!authToken, pendingGoogleSignup })

    // Deferred Google OAuth: user picked Google at the auth modal but we held off
    // so they could finish the quiz on the prelander host. Now fire the real OAuth
    // with model-params + path: '/paywall' baked into state — backend then redirects
    // straight to ud824.com/paywall?model-params=... (cookie auth carries the session).
    if (pendingGoogleSignup && !authToken) {
      let modelParams = null
      try {
        modelParams = await createModelParams(selections)
      } catch (err) {
        console.warn('[quiz] createModelParams failed, firing OAuth without it', err)
      }
      const utm = getStorage('utm_data') || {}
      const efTransactionId = getEfTransactionId()
      const externalClickIds = await getExternalClickIds()
      // Mirrors the sibling create-friend lander's Google OAuth state assembly:
      // utm + ef_transaction_id (conditional spread) + externalClickIds at the top level.
      // Encode protection=sfw into `path` so it ends up on the post-OAuth paywall URL —
      // backend builds the final URL via `new URL(BASE + path)`.
      const state = {
        ...utm,
        lp_source: LP_SOURCE,
        ...(efTransactionId ? { ef_transaction_id: efTransactionId } : {}),
        externalClickIds,
        // chat_paywall → /lobby?protection=sfw; else /paywall?protection=sfw.
        path: buildOAuthStatePath(getNavType()),
        type: welcome === 'Anime' ? 'anime' : 'realistic',
      }
      if (modelParams) state['model-params'] = modelParams
      const googleAuthUrl = getGoogleAuthUrl()
      const finalUrl = `${googleAuthUrl}?state=${encodeURIComponent(JSON.stringify(state))}`
      console.log('[quiz] Firing deferred Google OAuth →', finalUrl)
      localStorage.removeItem('pendingGoogleSignup')
      localStorage.setItem('isCompleted', true)
      window.location.href = finalUrl
      return
    }

    let paywallUrl = null

    if (authToken) {
      try {
        const modelParams = await createModelParams(selections)
        // protection=sfw is added inside buildPaywallUrl — create-friend-white is the SFW lander variant.
        paywallUrl = buildPaywallUrl(
          authToken,
          'login_paywall',
          modelParams,
          welcome,
          null,
        )
      } catch (err) {
        console.warn('Failed to build paywall URL', err)
      }
    }

    localStorage.setItem('isCompleted', true)
    if (paywallUrl) {
      window.location.href = paywallUrl
      return
    }

    const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL || ''
    const navType = getNavType()
    const fallbackUrl = new URL(`${SWIPEY_BASE_URL}${resolvePaywallPath(navType)}`)
    fallbackUrl.searchParams.set('from_landing', 'true')
    // create-friend-white is the SFW lander variant — every paywall handoff carries protection=sfw.
    fallbackUrl.searchParams.set('protection', 'sfw')
    window.location.href = fallbackUrl.toString()
  }, [isRedirecting, selections, welcome])

  // Resolve character media from value+welcome (source of truth) and fall back to
  // whatever was stashed on the selections object. This makes the final step robust
  // to stale localStorage from older builds where `character.video` may be a dead URL.
  const characterStyle = welcome === 'Anime' ? 'Anime' : 'Realistic'
  const resolvedMedia = character?.value ? CHARACTER_MEDIA[characterStyle]?.[character.value] : null
  const characterVideo = resolvedMedia?.video || character?.video
  const characterPoster = resolvedMedia?.poster || character?.poster
  const characterImage = character?.image // legacy fallback
  const characterLabel =
    (character?.value && CHARACTER_LABELS[character.value]) || character?.label || 'Your character'

  return (
    <div className='quizContentWrapper lastStep quiz-final-wrapper'>
      <div className='progress-stepper-text step5-ribbon'>
        <img src={giftIcon} alt='' width={12} height={12} />
        {t('limited_offer')}{' '}
        <span className='highlight-text'>{t('offer_price')}</span>
        <img src={giftIcon} alt='' width={12} height={12} />
      </div>
      <QuizContent className='step5-design quiz-final-design'>
        <QuizTitle>{t('your_companion_ready')}</QuizTitle>

        <div className='result-grid quiz-final-grid'>
          <div className='result-main'>
            {characterVideo ? (
              <video
                src={characterVideo}
                poster={characterPoster || undefined}
                autoPlay
                loop
                muted
                preload='metadata'
                playsInline
                controls={false}
                className='step0Video'
              />
            ) : characterPoster || characterImage ? (
              <LazyImage src={characterPoster || characterImage} alt={characterLabel} />
            ) : (
              <video
                src={VIDEOS[welcome] || VIDEOS.Realistic}
                autoPlay
                loop
                muted
                preload='metadata'
                playsInline
                controls={false}
                className='step0Video'
              />
            )}
            <div className='result-main-label'>
              {(characterVideo || characterPoster || characterImage) ? characterLabel : welcome}
            </div>
          </div>

          <div className='result-cards quiz-final-cards'>
            <div className='result-card result-card-wide result-card-welcome'>
              <video
                src={VIDEOS[welcome] || VIDEOS.Realistic}
                autoPlay
                loop
                muted
                playsInline
                preload='metadata'
                controls={false}
              />
              <span>{welcome}</span>
            </div>
            {personality && (
              <div className='result-card result-card-personality'>
                <div className='personality-icon-wrapper'>
                  {renderPersonalityIcon(personality)}
                </div>
                <span>{RELATIONSHIP_LABELS[personality] || personality}</span>
              </div>
            )}
            {personalityTrait && (
              <div className='result-card result-card-personality result-card-trait'>
                <div className='personality-icon-wrapper'>
                  {renderPersonalityTraitIcon(personalityTrait)}
                </div>
                <span>{personalityTraitLabel || personalityTrait}</span>
              </div>
            )}
          </div>
        </div>

        <div className='cta-container'>
          <button
            className='bring-to-life-button'
            onClick={handleBringToLife}
            disabled={isRedirecting}
          >
            <img src={starsIcon} alt='' className='sparkle-icon' width={20} height={20} />
            <span>{isRedirecting ? t('loading_text') : t('bring_ai')}</span>
          </button>
        </div>
      </QuizContent>
    </div>
  )
}

export default QuizFinal
